//ChartOptions.js - FIXED VERSION WITH PROPER DATE ORDERING
import { returnSortedAndAccumulatedNonPeakData } from '../../routes/DataView/GraphUtilities';

// Modern color palette with gradients
const modernColors = {
  // Modern flat colors for compatibility
  flat: {
    'yes': '#22c55e',
    'no': '#ef4444',
    '5.0': '#22c55e',
    '4.0': '#84cc16',
    '3.0': '#f59e0b',
    '2.0': '#fb923c',
    '1.0': '#ef4444'
  },
};

// Modern base theme configuration
const modernTheme = {
  textStyle: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: '#374151'
  },
  title: {
    textStyle: {
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: 18,
      fontWeight: 600,
      color: '#1f2937',
      lineHeight: 1.4
    },
    left: 'center',
    top: 16
  },
  grid: {
    borderWidth: 0,
    backgroundColor: 'transparent',
    left: '8%',
    right: '8%',
    top: '20%',
    bottom: '15%',
    containLabel: true
  },
  tooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderColor: 'rgba(99, 102, 241, 0.2)',
    borderWidth: 1,
    borderRadius: 12,
    textStyle: {
      color: '#1f2937',
      fontFamily: 'Inter, sans-serif',
      fontSize: 13
    },
    padding: [12, 16],
    extraCssText: 'backdrop-filter: blur(10px); box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);'
  },
  legend: {
    textStyle: {
      color: '#374151',
      fontSize: 13,
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500
    },
    bottom: 16,
    itemGap: 20
  }
};

const getModernAxisStyle = () => ({
  axisLine: {
    lineStyle: {
      color: '#e5e7eb',
      width: 1
    }
  },
  axisTick: {
    lineStyle: {
      color: '#e5e7eb'
    }
  },
  axisLabel: {
    color: '#6b7280',
    fontSize: 12,
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500
  },
  splitLine: {
    lineStyle: {
      color: '#f3f4f6',
      type: 'dashed',
      opacity: 0.6
    }
  }
});

const getRatingColor = (rating) => {
  const ratingValue = parseFloat(rating);
  if (ratingValue === 5.0) return '#10b981';
  if (ratingValue === 4.0) return '#84cc16';
  if (ratingValue === 3.0) return '#f59e0b';
  if (ratingValue === 2.0) return '#f97316';
  if (ratingValue === 1.0) return '#ef4444';
  return '#ef4444';
};

const responseColors = {
  'yes': '#10b981',
  'no': '#ef4444',
  '1.0': '#ef4444',
  '2.0': '#f97316',
  '3.0': '#f59e0b',
  '4.0': '#84cc16',
  '5.0': '#10b981'
};


const aggregateRawDataByHour = (rawData, dateRange) => {
  if (!rawData || rawData.length === 0) return [];
  
  // Create a map to store both the aggregated data and the original date for sorting
  const hourlyGroupsWithDates = {};
  
  rawData.forEach((item) => {
    try {
      // Parse the date directly without timezone conversion
      const dateTime = new Date(item.feedback_time || item.date);
      if (isNaN(dateTime.getTime())) {
        console.warn('Invalid date found:', item.feedback_time || item.date);
        return;
      }
      
      let timeKey;
      let sortKey;
      
      if (dateRange === 'today' || dateRange === 'yesterday') {
        const hour = dateTime.getHours();
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        timeKey = `${displayHour}${period}`;
        sortKey = hour; // Use 24-hour format for sorting
      } else if (dateRange === 'week') {
        // For weekly view, show the day of the week
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const dayOfWeek = dateTime.getDay();
        timeKey = days[dayOfWeek];
        // Adjust sort key so Monday=0, Tuesday=1, ..., Saturday=5, Sunday=6
        sortKey = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      } else {
        // For longer periods, use DD/MM format but store full date for sorting
        const day = dateTime.getDate().toString().padStart(2, '0');
        const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
        timeKey = `${day}/${month}`;
        sortKey = dateTime.getTime(); // Use timestamp for sorting
      }

      if (!hourlyGroupsWithDates[timeKey]) {
        hourlyGroupsWithDates[timeKey] = {
          yesCount: 0,
          noCount: 0,
          totalCount: 0,
          sortKey: sortKey,
          originalDate: dateTime
        };
      }
      
      const score = parseFloat(item.value);
      if (score >= 3 && score <= 5) {
        hourlyGroupsWithDates[timeKey].yesCount++;
      } else if (score >= 1 && score < 3) {
        hourlyGroupsWithDates[timeKey].noCount++;
      }
      hourlyGroupsWithDates[timeKey].totalCount++;
    } catch (error) {
      console.error(`Error processing feedback item:`, error, item);
    }
  });

  // Convert to array format and sort properly
  const result = Object.entries(hourlyGroupsWithDates).map(([timeKey, data]) => [
    timeKey, 
    {
      yesCount: data.yesCount,
      noCount: data.noCount,
      totalCount: data.totalCount
    },
    data.sortKey // Include sort key for proper ordering
  ]);
  
  // Sort based on the sort key
  result.sort((a, b) => a[2] - b[2]);
  
  // Remove the sort key from the final result
  return result.map(([timeKey, counts]) => [timeKey, counts]);
};

export const getChartOptions = (title, data, type, peaks, dateRange, happyPeriodStart, happyPeriodEnd, unhappyPeriodStart, unhappyPeriodEnd) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {
      ...modernTheme,
      title: {
        ...modernTheme.title,
        text: title || 'No Data Available',
        textStyle: {
          ...modernTheme.title.textStyle,
          color: '#9ca3af'
        }
      },
      xAxis: {
        type: 'category',
        data: [],
        ...getModernAxisStyle()
      },
      yAxis: {
        type: 'value',
        ...getModernAxisStyle()
      },
      series: [{
        data: [],
        type: 'bar',
        itemStyle: {
          color: '#e5e7eb'
        }
      }]
    };
  }

  const isAggregatedData = data.length > 0 &&
    data[0] &&
    typeof data[0] === 'object' &&
    'name' in data[0] &&
    'value' in data[0];

  if (isAggregatedData && data.some(item => !isNaN(parseFloat(item.name)))) {
    const sortedData = [...data].sort((a, b) => parseFloat(b.name) - parseFloat(a.name));
    return {
      ...modernTheme,
      title: {
        ...modernTheme.title,
        text: title
      },
      tooltip: {
        ...modernTheme.tooltip,
        trigger: 'axis',
        formatter: function(params) {
          const param = params[0];
          return `<div style="font-weight: 600; margin-bottom: 4px;">Rating ${param.name}</div>
                  <div style="color: #6b7280;">Responses: <span style="font-weight: 600; color: ${param.color};">${param.value}</span></div>`;
        }
      },
      xAxis: {
        type: 'category',
        data: sortedData.map(item => item.name),
        name: 'Rating',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          color: '#374151',
          fontSize: 13,
          fontWeight: 600
        },
        ...getModernAxisStyle(),
        axisLabel: {
          ...getModernAxisStyle().axisLabel,
          interval: 0,
          rotate: 0,
          margin: 12
        }
      },
      yAxis: {
        type: 'value',
        name: 'Count',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          color: '#374151',
          fontSize: 13,
          fontWeight: 600
        },
        ...getModernAxisStyle()
      },
      series: [{
        data: sortedData.map((item) => ({
          value: item.value,
          itemStyle: {
            color: getRatingColor(item.name),
            borderRadius: [4, 4, 0, 0],
            shadowBlur: 8,
            shadowColor: 'rgba(0, 0, 0, 0.1)',
            shadowOffsetY: 2
          }
        })),
        type: type || 'bar',
        barMaxWidth: '60%',
        barGap: '20%',
        label: {
          show: true,
          position: 'top',
          formatter: '{c}',
          color: '#374151',
          fontSize: 12,
          fontWeight: 600,
          distance: 8
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 15,
            shadowColor: 'rgba(0, 0, 0, 0.2)'
          }
        },
        animationDelay: function(idx) {
          return idx * 100;
        }
      }]
    };
  }

  if (isAggregatedData && data.some(item => ['Yes', 'No', 'yes', 'no'].includes(item.name))) {
    return {
      ...modernTheme,
      title: {
        ...modernTheme.title,
        text: title
      },
      tooltip: {
        ...modernTheme.tooltip,
        trigger: 'item',
        formatter: function(params) {
          return `<div style="font-weight: 600; margin-bottom: 4px;">${params.name}</div>
                  <div style="color: #6b7280;">Count: <span style="font-weight: 600; color: ${params.color};">${params.value}</span></div>
                  <div style="color: #6b7280;">Percentage: <span style="font-weight: 600;">${params.percent}%</span></div>`;
        }
      },
      legend: {
        ...modernTheme.legend,
        data: data.map(item => item.name)
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        data: data.map(item => ({
          name: item.name,
          value: item.value,
          itemStyle: {
            color: responseColors[item.name.toLowerCase()] || '#6b7280',
            borderRadius: 8,
            borderColor: 'rgba(255, 255, 255, 0.8)',
            borderWidth: 2,
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          }
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 20,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
            scale: true,
            scaleSize: 5
          },
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 'bold'
          }
        },
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}: {c} ({d}%)',
          fontSize: 13,
          fontWeight: 500,
          color: '#374151'
        },
        labelLine: {
          show: true,
          length: 15,
          length2: 20,
          smooth: true
        },
        animationType: 'scale',
        animationEasing: 'elasticOut'
      }]
    };
  }

  if (type === 'line') {
    const isRawFeedbackData = data.length > 0 &&
      data[0] &&
      typeof data[0] === 'object' &&
      'feedback_time' in data[0] &&
      'value' in data[0];

    let sortedData;
    if (isRawFeedbackData) {
      sortedData = aggregateRawDataByHour(data, dateRange);
    } else {
      sortedData = returnSortedAndAccumulatedNonPeakData(data, dateRange);
    }

    const xAxisData = sortedData.map(item => item[0]);
    const yesData = sortedData.map(item => (item[1] && item[1].yesCount) || 0);
    const noData = sortedData.map(item => (item[1] && item[1].noCount) || 0);

    const baseLineConfig = {
      ...modernTheme,
      title: {
        ...modernTheme.title,
        text: title
      },
      tooltip: {
        ...modernTheme.tooltip,
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6366f1',
            borderRadius: 6
          }
        },
        formatter: function(params) {
          let result = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;
          params.forEach(param => {
            result += `<div style="display: flex; align-items: center; margin-bottom: 4px;">
                        <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                        <span style="color: #6b7280;">${param.seriesName}: </span>
                        <span style="font-weight: 600; margin-left: 4px;">${param.value}</span>
                       </div>`;
          });
          return result;
        }
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        boundaryGap: false,
        ...getModernAxisStyle(),
        axisLabel: {
          ...getModernAxisStyle().axisLabel,
          interval: 0,
          rotate: 0,
          formatter: function(value) {
            // The value is already properly formatted from aggregateRawDataByHour
            return value;
          }
        }
      },
      yAxis: {
        type: 'value',
        ...getModernAxisStyle(),
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(250, 250, 250, 0.3)', 'rgba(245, 245, 245, 0.3)']
          }
        }
      },
      legend: {
        ...modernTheme.legend,
        data: ['Happy responses', 'Unhappy responses']
      }
    };

    if (peaks === true) {
      return {
        ...baseLineConfig,
        series: [{
          data: yesData,
          type: 'line',
          smooth: true,
          name: 'Happy responses',
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 3,
            color: modernColors.flat.yes
          },
          itemStyle: {
            color: modernColors.flat.yes,
            borderColor: '#fff',
            borderWidth: 2
          },
          emphasis: {
            focus: 'series'
          }
        }, {
          data: noData,
          type: 'line',
          smooth: true,
          name: 'Unhappy responses',
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 3,
            color: modernColors.flat.no
          },
          itemStyle: {
            color: modernColors.flat.no,
            borderColor: '#fff',
            borderWidth: 2
          },
          emphasis: {
            focus: 'series'
          }
        }, {
          type: 'line',
          data: [],
          markArea: happyPeriodStart && happyPeriodEnd ? {
            itemStyle: {
              color: 'rgba(16, 185, 129, 0.15)',
              borderColor: 'rgba(16, 185, 129, 0.3)',
              borderWidth: 1,
              borderType: 'dashed'
            },
            data: [
              [{
                name: "Happy Peak",
                xAxis: happyPeriodStart
              }, {
                xAxis: happyPeriodEnd
              }]
            ],
            label: {
              show: true,
              position: 'top',
              formatter: 'Happy Peak',
              color: '#059669',
              fontSize: 12,
              fontWeight: 600
            }
          } : undefined,
          z: 1
        }, {
          type: 'line',
          data: [],
          markArea: unhappyPeriodStart && unhappyPeriodEnd ? {
            itemStyle: {
              color: 'rgba(239, 68, 68, 0.15)',
              borderColor: 'rgba(239, 68, 68, 0.3)',
              borderWidth: 1,
              borderType: 'dashed'
            },
            data: [
              [{
                name: "Unhappy Peak",
                xAxis: unhappyPeriodStart
              }, {
                xAxis: unhappyPeriodEnd
              }]
            ],
            label: {
              show: true,
              position: 'bottom',
              formatter: 'Unhappy Peak',
              color: '#dc2626',
              fontSize: 12,
              fontWeight: 600
            }
          } : undefined,
          z: 1
        }]
      };
    } else {
      return {
        ...baseLineConfig,
        series: [{
          data: yesData,
          type: 'line',
          smooth: true,
          name: 'Happy responses',
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 3,
            color: modernColors.flat.yes
          },
          itemStyle: {
            color: modernColors.flat.yes,
            borderColor: '#fff',
            borderWidth: 2
          },
          emphasis: {
            focus: 'series'
          }
        }, {
          data: noData,
          type: 'line',
          smooth: true,
          name: 'Unhappy responses',
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 3,
            color: modernColors.flat.no
          },
          itemStyle: {
            color: modernColors.flat.no,
            borderColor: '#fff',
            borderWidth: 2
          },
          emphasis: {
            focus: 'series'
          }
        }]
      };
    }
  }

  if (type === 'line2') {
    if (!data || data.length === 0) {
      return {
        ...modernTheme,
        title: {
          ...modernTheme.title,
          text: title || 'No Business Data Available'
        },
        xAxis: {
          type: 'category',
          data: [],
          ...getModernAxisStyle()
        },
        yAxis: {
          type: 'value',
          ...getModernAxisStyle()
        },
        series: []
      };
    }

    const siteNames = new Set();
    data.forEach(entry => {
      Object.keys(entry).forEach(key => {
        if (!['date', 'week_starting'].includes(key) &&
          !key.includes('_count') &&
          !key.includes('_score') &&
          !key.includes('_vs_prev') &&
          !key.includes('_Hills_') &&
          !key.includes('_Prospect_') &&
          !key.includes('_Rd_')) {
          siteNames.add(key);
        }
      });
    });

    const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
    const dates = sortedData.map(entry => entry.date);

    const series = Array.from(siteNames).map((siteName, index) => {
      const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];
      const color = colors[index % colors.length];

      const seriesData = sortedData.map(entry => {
        const value = entry[siteName];
        return (value !== undefined && value !== null) ? value : 0;
      });

      return {
        name: siteName,
        type: 'line',
        smooth: true,
        data: seriesData,
        lineStyle: {
          width: 3,
          color: color
        },
        itemStyle: {
          color: color,
          borderColor: '#fff',
          borderWidth: 2
        },
        symbol: 'circle',
        symbolSize: 6,
        emphasis: {
          focus: 'series',
          itemStyle: {
            shadowBlur: 10,
            shadowColor: color
          }
        },
        animationDelay: function(idx) {
          return idx * 50;
        }
      };
    });

    return {
      ...modernTheme,
      title: {
        ...modernTheme.title,
        text: title
      },
      tooltip: {
        ...modernTheme.tooltip,
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6366f1',
            borderRadius: 6
          }
        },
        formatter: function(params) {
          let result = `<div style="font-weight: 600; margin-bottom: 8px;">Week: ${params[0].axisValue}</div>`;
          params.forEach(param => {
            result += `<div style="display: flex; align-items: center; margin-bottom: 4px;">
                        <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                        <span style="color: #6b7280;">${param.seriesName}: </span>
                        <span style="font-weight: 600; margin-left: 4px;">${typeof param.value === 'number' ? param.value.toFixed(1) : param.value}</span>
                       </div>`;
          });
          return result;
        }
      },
      xAxis: {
        type: 'category',
        data: dates.map(date => {
          try {
            const dateObj = new Date(date);
            if (!isNaN(dateObj.getTime())) {
              const day = dateObj.getDate().toString().padStart(2, '0');
              const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
              return `${day}/${month}`;
            }
          } catch (e) {
            console.warn('Date parsing error for date:', date);
          }
          return date ? date.toString().substring(0, 10) : 'Unknown';
        }),
        boundaryGap: false,
        ...getModernAxisStyle(),
        axisLabel: {
          ...getModernAxisStyle().axisLabel,
          rotate: dates.length > 6 ? 30 : 0,
          interval: Math.max(0, Math.floor(dates.length / 8))
        }
      },
      yAxis: {
        type: 'value',
        name: 'Feedback Score',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          color: '#374151',
          fontSize: 13,
          fontWeight: 600
        },
        min: 0,
        max: 5,
        interval: 1,
        ...getModernAxisStyle(),
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(250, 250, 250, 0.3)', 'rgba(245, 245, 245, 0.3)']
          }
        }
      },
      legend: {
        ...modernTheme.legend,
        data: Array.from(siteNames),
        type: 'scroll',
        pageButtonItemGap: 5,
        pageButtonGap: 20
      },
      series: series,
      animation: true,
      animationDuration: 1000,
      animationEasing: 'cubicOut'
    };
  }

  const categories = Array.from(new Set(data));
  const seriesData = categories.map(category => data.filter(item => item === category).length);

  return {
    ...modernTheme,
    title: {
      ...modernTheme.title,
      text: title
    },
    tooltip: {
      ...modernTheme.tooltip,
      trigger: 'item',
      formatter: type === 'pie' ? '{b}: {c} ({d}%)' : '{b}: {c}'
    },
    xAxis: {
      type: 'category',
      data: categories,
      ...getModernAxisStyle()
    },
    yAxis: {
      type: 'value',
      ...getModernAxisStyle()
    },
    series: [{
      data: type === 'pie' ?
        seriesData.map((value, index) => ({
          value,
          name: categories[index],
          itemStyle: {
            color: responseColors[categories[index]] || '#6b7280'
          }
        })) :
        seriesData.map((value, index) => ({
          value,
          itemStyle: {
            color: responseColors[categories[index]] || '#6b7280'
          }
        })),
      type: type,
      barMaxWidth: '60%'
    }]
  };
};